import { ReactNode } from "react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { OrderingPlatformType } from "__generated__/gql/graphql";
import EmptyPlaceholder, { NA } from "components/Shared/EmptyPlaceholder";
import ExternalDomainLink from "components/Shared/ExternalDomainLink";
import getMaybeList from "components/Shared/getMaybeList";
import LabeledValueTable, {
  LabeledValueRow,
  LabeledValueTableProps,
} from "components/Shared/LabeledValueTable";
import Link from "components/Shared/Link";
import MarkdownText from "components/Shared/MarkdownText";
import MaxHeightExpander from "components/Shared/MaxHeightExpander";
import MaybeButtonLink from "components/Shared/MaybeButtonLink";
import OrderingPlatformTypeBadge from "components/Shared/OrderingPlatformDirectory/OrderingPlatformTypeBadge";
import { NameWithFlagIcons } from "components/Shared/RetailerDirectory/utils";
import { assertPresence } from "utils/assertions";
import { adminRetailerPage, Url } from "utils/links";

const fragment = gql(/* GraphQL */ `
  fragment OrderingPlatformDetails on OrderingPlatform {
    id
    name
    orderingPlatformType
    website
    description
    directRetailerId
    directRetailer {
      id
      ...RetailerNameWithFlagIconsFragment
    }
    isParent
    parentId
    parent {
      id
      name
    }
    childOrderingPlatformsList(orderBy: NAME_ASC) {
      id
      name
    }
  }
`);

type Props = Omit<LabeledValueTableProps, "isLoaded"> & {
  children?: ReactNode;
  data: FragmentType<typeof fragment> | null | undefined;
  getOrderingPlatformLink?: (orderingPlatformId: UUID) => string | Url;
  isAdmin?: boolean;
  isLoading?: boolean;
  isNameIncluded?: boolean;
  onSelectedOrderingPlatformIdChange?: (id: UUID) => void;
};

export default function OrderingPlatformDetails({
  children,
  data,
  getOrderingPlatformLink,
  isAdmin = false,
  isLoading,
  isNameIncluded,
  onSelectedOrderingPlatformIdChange,
  ...rest
}: Props) {
  const orderingPlatform = getFragmentData(fragment, data);

  return (
    <LabeledValueTable
      sx={{ tableLayout: "fixed" }}
      cellProps={{ py: 2 }}
      labelProps={{ width: "10em", textAlign: "right" }}
      isLoaded={!isLoading}
      {...rest}
    >
      {isNameIncluded && (
        <LabeledValueRow label="Name" value={orderingPlatform?.name} />
      )}

      <LabeledValueRow
        label="Type"
        value={
          orderingPlatform?.orderingPlatformType && (
            <OrderingPlatformTypeBadge
              orderingPlatformType={orderingPlatform.orderingPlatformType}
              alignSelf="flex-start"
            />
          )
        }
      />

      {orderingPlatform?.orderingPlatformType ===
        OrderingPlatformType.DIRECT_RETAILER && (
        <LabeledValueRow
          label="Direct Retailer"
          value={
            orderingPlatform?.directRetailer && (
              <Link
                href={
                  isAdmin
                    ? adminRetailerPage(orderingPlatform.directRetailer.id)
                    : undefined
                }
              >
                <NameWithFlagIcons retailer={orderingPlatform.directRetailer} />
              </Link>
            )
          }
        />
      )}

      {!!(orderingPlatform?.parent || orderingPlatform?.isParent) && (
        <LabeledValueRow
          label="Parent"
          value={
            <>
              {orderingPlatform?.parent &&
                (getOrderingPlatformLink ? (
                  <Link
                    href={getOrderingPlatformLink(orderingPlatform.parent.id)}
                  >
                    {orderingPlatform.parent.name}
                  </Link>
                ) : (
                  <MaybeButtonLink
                    onClick={
                      onSelectedOrderingPlatformIdChange
                        ? () => {
                            assertPresence(orderingPlatform.parentId);
                            onSelectedOrderingPlatformIdChange(
                              orderingPlatform.parentId,
                            );
                          }
                        : undefined
                    }
                  >
                    {orderingPlatform.parent.name}
                  </MaybeButtonLink>
                ))}

              {orderingPlatform?.isParent && (
                <EmptyPlaceholder fontStyle="italic">
                  Customer is a Parent
                </EmptyPlaceholder>
              )}

              {!orderingPlatform?.parent && !orderingPlatform?.isParent && (
                <NA />
              )}
            </>
          }
        />
      )}

      <LabeledValueRow
        label="Website"
        value={
          orderingPlatform?.website && (
            <ExternalDomainLink href={orderingPlatform.website} isTruncated>
              <ExternalLinkIcon mx={1} mt={-1} />
            </ExternalDomainLink>
          )
        }
      />

      <LabeledValueRow
        label="Description"
        value={
          orderingPlatform?.description && (
            <MaxHeightExpander>
              <MarkdownText markdown={orderingPlatform.description} />
            </MaxHeightExpander>
          )
        }
        rowProps={{ gridColumn: "1/-1", order: 1 }}
      />

      {orderingPlatform?.isParent && (
        <LabeledValueRow
          label={`Sub-Customer${
            orderingPlatform.childOrderingPlatformsList?.length === 1 ? "" : "s"
          }`}
          value={
            orderingPlatform?.childOrderingPlatformsList
              ? orderingPlatform.childOrderingPlatformsList.length > 0 && (
                  <MaxHeightExpander lineHeight="normal">
                    {getMaybeList(
                      orderingPlatform.childOrderingPlatformsList.map((op) =>
                        getOrderingPlatformLink ? (
                          <Link
                            key={op.id}
                            href={getOrderingPlatformLink(op.id)}
                          >
                            {op.name}
                          </Link>
                        ) : (
                          <MaybeButtonLink
                            key={op.id}
                            onClick={
                              onSelectedOrderingPlatformIdChange
                                ? () => {
                                    assertPresence(op.id);
                                    onSelectedOrderingPlatformIdChange(op.id);
                                  }
                                : undefined
                            }
                          >
                            {op.name}
                          </MaybeButtonLink>
                        ),
                      ),
                    )}
                  </MaxHeightExpander>
                )
              : undefined
          }
        />
      )}

      {children}
    </LabeledValueTable>
  );
}
